import { ReportingMixpanelEvent } from '@peekapak/types/ReportDataExchangeTypes';
import associationData from '../../ClassroomDashboard/Associations.json';
import {
  getLegacyStoryOpenEvents,
  getLegacyStoryOpens,
  getNewStoryOpenEvents,
  getNewStoryOpens,
} from '../reportUtilities';
import {
  ELEMENTARY_MOODS,
  SECONDARY_MOOD_CATEGORIES,
  JournalMapEntry,
  MoodLogEvent,
  isLegacyStudentLoginEvent,
  isNewStudentLoginEvent,
  monthKeyOf,
  monthLabelOf,
  monthsInRange,
  resolveElementaryMood,
  resolveJournalEntry,
  resolveSecondaryCategory,
} from './schoolReportDeck';

export interface ClassroomDeckMetrics {
  title: string;
  dateRangeLabel: string;
  studentLoginsTotal: number;
  studentLoginsByMonth: { month: string; logins: number }[];
  elementaryMoods: { mood: string; percent: number }[];
  elementaryUpMoodPercent: number;
  elementaryStudents: number;
  elementaryMoodsByMonth: { month: string; counts: Record<string, number> }[];
  secondaryMoods: { mood: string; percent: number }[];
  secondaryUpMoodPercent: number;
  secondaryStudents: number;
  secondaryMoodsByMonth: { month: string; counts: Record<string, number> }[];
  topGames: { name: string; plays: number }[];
  activitiesCount: number;
  topStories: { name: string; reads: number }[];
  storiesCount: number;
}

export interface ClassroomDeckMetricsInput {
  // The classroom's name, or "All Classroom Data" when the "download all classrooms" button was
  // used - the deck's cover slide shows this directly rather than the school name.
  title: string;
  startDate: Date | null;
  endDate: Date | null;
  // The scoped student population - the selected classroom's students, or the union of every one
  // of the teacher's (grade-filtered) classrooms' students, depending on which download button was
  // clicked. Every other field below is filtered down to just these userIds.
  studentIds: string[];
  // userId -> roster grade, used the same way schoolReportDeck.ts falls back to a roster grade for
  // journal entries that don't carry their own grade.
  gradeLookup: Map<string, number | string | undefined>;
  // Unfiltered event pool (mirrors the school deck's legacyPeekavilleEvents+legacyPlatformEvents+
  // newMixpanelEvents concatenation) - filtered down to studentIds internally.
  allEvents: ReportingMixpanelEvent[];
  journalsMap: Record<string, JournalMapEntry[]>;
  gameProgressMap?: Record<
    string,
    Array<{
      completionPercent: number;
      displayName: string;
      id: string;
      lastDateOpened: number;
      readingLevel: number;
      timeSpent: number;
      type: string;
    }>
  >;
}

const parseGrade = (raw: unknown): number | undefined => {
  const grade = parseInt(String(raw ?? ''), 10);
  return Number.isNaN(grade) ? undefined : grade;
};

const emptyElementaryMonth = () => ({
  Excited: 0,
  Happy: 0,
  Calm: 0,
  Nervous: 0,
  Sad: 0,
  Angry: 0,
});

const emptySecondaryMonth = () => ({
  Surprised: 0,
  Confident: 0,
  Happy: 0,
  Calm: 0,
  Sad: 0,
  Scared: 0,
  Angry: 0,
});

export const computeClassroomDeckMetrics = (input: ClassroomDeckMetricsInput): ClassroomDeckMetrics => {
  const { title, startDate, endDate, studentIds, gradeLookup, allEvents, journalsMap, gameProgressMap } = input;

  const studentIdSet = new Set(studentIds);
  const studentEvents = (allEvents || []).filter(
    (event) => event.type === 'Student' && studentIdSet.has(event.userId || event.distinct_id),
  );

  const studentLoginEvents = [
    ...studentEvents.filter(isLegacyStudentLoginEvent),
    ...studentEvents.filter(isNewStudentLoginEvent),
  ];

  const studentByMonth: Record<string, number> = {};
  studentLoginEvents.forEach((event) => {
    const key = monthKeyOf(event.time);
    studentByMonth[key] = (studentByMonth[key] || 0) + 1;
  });
  const allMonthKeys = Object.keys(studentByMonth).sort();
  const rangeMonthKeys = monthsInRange(startDate, endDate);
  const monthKeys = rangeMonthKeys.length ? rangeMonthKeys : allMonthKeys;
  const studentLoginsByMonth = monthKeys.map((key) => ({
    month: monthLabelOf(key),
    logins: studentByMonth[key] || 0,
  }));

  const elementaryMoodCounts: Record<string, number> = {
    Excited: 0,
    Happy: 0,
    Calm: 0,
    Nervous: 0,
    Sad: 0,
    Angry: 0,
  };
  const secondaryMoodCounts: Record<string, number> = {
    Surprised: 0,
    Confident: 0,
    Happy: 0,
    Calm: 0,
    Sad: 0,
    Scared: 0,
    Angry: 0,
  };

  let totalElementaryCheckIns = 0;
  let totalSecondaryCheckIns = 0;
  const elementaryByMonth: Record<string, Record<string, number>> = {};
  const secondaryByMonth: Record<string, Record<string, number>> = {};
  const ensureElementaryMonth = (month: string) => {
    if (!elementaryByMonth[month]) elementaryByMonth[month] = emptyElementaryMonth();
    return elementaryByMonth[month];
  };
  const ensureSecondaryMonth = (month: string) => {
    if (!secondaryByMonth[month]) secondaryByMonth[month] = emptySecondaryMonth();
    return secondaryByMonth[month];
  };

  const elementaryCheckedInUsers = new Set<string>();
  const secondaryCheckedInUsers = new Set<string>();

  Object.entries(journalsMap || {}).forEach(([userId, entries]) => {
    if (!studentIdSet.has(userId)) return;
    const rosterGrade = gradeLookup.get(userId);
    (entries || []).forEach((entry) => {
      const resolved = resolveJournalEntry(entry);
      if (!resolved) return;
      const gradeNum = parseGrade(entry.grade) ?? parseGrade(rosterGrade);
      if (gradeNum === undefined) return;
      const month = monthKeyOf(resolved.time);
      if (gradeNum <= 5) {
        const mood = resolveElementaryMood(resolved.rawMood);
        if (mood && Object.prototype.hasOwnProperty.call(elementaryMoodCounts, mood)) {
          elementaryMoodCounts[mood]++;
          totalElementaryCheckIns++;
          ensureElementaryMonth(month)[mood]++;
          elementaryCheckedInUsers.add(userId);
        }
      } else if (gradeNum >= 6) {
        const category = typeof resolved.rawMood === 'string' ? resolveSecondaryCategory(resolved.rawMood) : undefined;
        if (category && Object.prototype.hasOwnProperty.call(secondaryMoodCounts, category)) {
          secondaryMoodCounts[category]++;
          totalSecondaryCheckIns++;
          ensureSecondaryMonth(month)[category]++;
          secondaryCheckedInUsers.add(userId);
        }
      }
    });
  });

  const elementaryMoodsByMonth = monthKeys.map((month) => ({
    month: monthLabelOf(month),
    counts: elementaryByMonth[month] || emptyElementaryMonth(),
  }));
  const secondaryMoodsByMonth = monthKeys.map((month) => ({
    month: monthLabelOf(month),
    counts: secondaryByMonth[month] || emptySecondaryMonth(),
  }));

  const elementaryMoods = ELEMENTARY_MOODS.map((mood) => ({
    mood,
    percent: totalElementaryCheckIns ? Math.round((elementaryMoodCounts[mood] / totalElementaryCheckIns) * 100) : 0,
  }));
  const elementaryUpMoodTotal =
    elementaryMoodCounts['Excited'] + elementaryMoodCounts['Happy'] + elementaryMoodCounts['Calm'];
  const elementaryUpMoodPercent = totalElementaryCheckIns
    ? Math.round((elementaryUpMoodTotal / totalElementaryCheckIns) * 100)
    : 0;

  const secondaryMoods = SECONDARY_MOOD_CATEGORIES.map((mood) => ({
    mood,
    percent: totalSecondaryCheckIns ? Math.round((secondaryMoodCounts[mood] / totalSecondaryCheckIns) * 100) : 0,
  }));
  const secondaryUpMoodTotal =
    secondaryMoodCounts['Surprised'] +
    secondaryMoodCounts['Confident'] +
    secondaryMoodCounts['Happy'] +
    secondaryMoodCounts['Calm'];
  const secondaryUpMoodPercent = totalSecondaryCheckIns
    ? Math.round((secondaryUpMoodTotal / totalSecondaryCheckIns) * 100)
    : 0;

  const elementaryStudents = elementaryCheckedInUsers.size;
  const secondaryStudents = secondaryCheckedInUsers.size;

  const gameAssociations = (associationData as { associations: Record<string, { displayName: string }> }).associations;
  const getDisplayName = (activityId: string): string => {
    const entry = gameAssociations[activityId];
    if (entry) return entry.displayName;
    const parts = activityId.match(/([A-Z]?[a-z]+)/g);
    if (parts) return parts.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    return activityId;
  };

  const gameCounts: Record<string, number> = {};
  Object.entries(gameProgressMap || {}).forEach(([userId, progress]) => {
    if (!studentIdSet.has(userId) || !Array.isArray(progress)) return;
    progress.forEach((entry) => {
      if (!entry) return;
      const isCounted =
        (entry.type === 'quest' && entry.completionPercent > 0) ||
        (['forever', 'daily', 'finite'].includes(entry.type) && entry.timeSpent > 0);
      if (isCounted) {
        const name = getDisplayName(entry.displayName || entry.id || '');
        gameCounts[name] = (gameCounts[name] || 0) + 1;
      }
    });
  });
  const topGames = Object.entries(gameCounts)
    .map(([name, plays]) => ({ name, plays }))
    .sort((a, b) => b.plays - a.plays)
    .slice(0, 10);
  const activitiesCount = Object.values(gameCounts).reduce((sum, count) => sum + count, 0);

  const bookNumberToDisplayName: Record<string, string> = {};
  Object.entries(gameAssociations).forEach(([key, entry]) => {
    if (key.startsWith('book_')) {
      bookNumberToDisplayName[key] = entry.displayName;
    }
  });
  const storyEvents = [...getLegacyStoryOpenEvents(studentEvents), ...getNewStoryOpenEvents(studentEvents)];
  const storyCounts: Record<string, number> = {};
  storyEvents.forEach((event) => {
    const storyEvent = event as MoodLogEvent;
    let name = event.name;
    const book = storyEvent.properties?.book ?? storyEvent.book;
    if (book !== null && book !== undefined && bookNumberToDisplayName[`book_${book}`]) {
      name = bookNumberToDisplayName[`book_${book}`];
    } else if (storyEvent.properties?.unit || storyEvent.unit) {
      name = storyEvent.properties?.unit || storyEvent.unit || name;
    } else if (storyEvent.story) {
      name = storyEvent.story;
    } else if (storyEvent.quest) {
      name = storyEvent.quest;
    } else {
      const legacyMatch = event.name.match(/^story(\d+)pg1/);
      if (legacyMatch && bookNumberToDisplayName[`book_${legacyMatch[1]}`]) {
        name = bookNumberToDisplayName[`book_${legacyMatch[1]}`];
      } else {
        const featuredMatch = event.name.match(/^featuredstory[_ ](.+)/i);
        if (featuredMatch) {
          name = featuredMatch[1].replace(/[_-]/g, ' ');
        }
      }
    }
    storyCounts[name] = (storyCounts[name] || 0) + 1;
  });
  const topStories = Object.entries(storyCounts)
    .map(([name, reads]) => ({ name, reads }))
    .sort((a, b) => b.reads - a.reads)
    .slice(0, 10);
  const storiesCount = getLegacyStoryOpens(studentEvents) + getNewStoryOpens(studentEvents);

  const dateRangeLabel = `${startDate ? startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''} - ${endDate ? endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}`;

  return {
    title,
    dateRangeLabel,
    studentLoginsTotal: studentLoginEvents.length,
    studentLoginsByMonth,
    elementaryMoods,
    elementaryUpMoodPercent,
    elementaryStudents,
    elementaryMoodsByMonth,
    secondaryMoods,
    secondaryUpMoodPercent,
    secondaryStudents,
    secondaryMoodsByMonth,
    topGames,
    activitiesCount,
    topStories,
    storiesCount,
  };
};
